import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface Societe {
  symbole: string;
  nom_societe: string;
  secteur: string;
  compartiment: string;
  pays?: string;
  // Capitalisation fields
  quantite_totale_titres?: number | null;
  quantite_titres_flottant?: number | null;
  flottant_pct?: number | null;
  diffusion_15pct_respectee?: boolean | null;
}

const AdminSocietesScreen: React.FC = () => {
  const { token } = useAuth();
  const [societes, setSocietes] = useState<Societe[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSociete, setEditingSociete] = useState<Societe | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSecteur, setFilterSecteur] = useState<string>('');
  const [filterPays, setFilterPays] = useState<string>('');
  const [filterConformite, setFilterConformite] = useState<string>('');

  const fetchSocietes = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/v1/stocks/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSocietes(Array.isArray(data) ? data : []);
      } else {
        console.error('Error fetching societes: HTTP', response.status);
        setSocietes([]);
      }
    } catch (error) {
      console.error('Error fetching societes:', error);
      setSocietes([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchSocietes();
    }
  }, [token, fetchSocietes]);

  const handleEdit = (societe: Societe) => {
    setEditingSociete({ ...societe });
  };

  const handleSave = async () => {
    if (!editingSociete) return;

    try {
      const response = await fetch(`${API_URL}/v1/admin/societes/${editingSociete.symbole}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editingSociete),
      });

      if (response.ok) {
        fetchSocietes();
        setEditingSociete(null);
      } else {
        alert('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating societe:', error);
    }
  };

  const formatNumber = (num: number | null | undefined) => {
    if (num === null || num === undefined) return '—';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)} Mrd`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)} M`;
    return num.toLocaleString('fr-FR');
  };

  // Extract unique sectors and countries for filters
  const sectors = [...new Set(societes.map(s => s.secteur).filter(Boolean))].sort();
  const countries = [...new Set(societes.map(s => s.pays).filter(Boolean))].sort();

  const filteredSocietes = societes.filter(soc => {
    // Search filter
    const matchesSearch = soc.symbole.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (soc.nom_societe || '').toLowerCase().includes(searchQuery.toLowerCase());

    // Sector filter
    const matchesSecteur = !filterSecteur || soc.secteur === filterSecteur;

    // Country filter
    const matchesPays = !filterPays || soc.pays === filterPays;

    // Conformity filter
    let matchesConformite = true;
    if (filterConformite === 'conforme') {
      matchesConformite = soc.diffusion_15pct_respectee === true;
    } else if (filterConformite === 'non_conforme') {
      matchesConformite = soc.diffusion_15pct_respectee === false;
    } else if (filterConformite === 'non_renseigne') {
      matchesConformite = soc.diffusion_15pct_respectee === null || soc.diffusion_15pct_respectee === undefined;
    }

    return matchesSearch && matchesSecteur && matchesPays && matchesConformite;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setFilterSecteur('');
    setFilterPays('');
    setFilterConformite('');
  };

  const hasActiveFilters = searchQuery || filterSecteur || filterPays || filterConformite;

  return (
    <SiteLayout>
      <Header icon='business' title='Gestion des Sociétés' />
      <Box>
        <div className='box-title box-vertical-padding box-horizontal-padding'>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <p>Liste des sociétés ({filteredSocietes.length}/{societes.length})</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="🔍 Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', width: '180px' }}
              />
              <select
                value={filterSecteur}
                onChange={(e) => setFilterSecteur(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '150px' }}
              >
                <option value="">Tous secteurs</option>
                {sectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={filterPays}
                onChange={(e) => setFilterPays(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '120px' }}
              >
                <option value="">Tous pays</option>
                {countries.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select
                value={filterConformite}
                onChange={(e) => setFilterConformite(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '140px' }}
              >
                <option value="">Toutes conformités</option>
                <option value="conforme">✅ Conformes 15%</option>
                <option value="non_conforme">❌ Non-conformes</option>
                <option value="non_renseigne">⚪ Non renseigné</option>
              </select>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fee2e2', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}
                >
                  ✕ Réinitialiser
                </button>
              )}
            </div>
          </div>
        </div>
        <div className='box-content box-horizontal-padding' style={{ padding: '20px', overflowX: 'auto' }}>
          {loading ? (
            <p>Chargement...</p>
          ) : (
            <table className='data-table' style={{ width: '100%', minWidth: '900px' }}>
              <thead>
                <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'left' }}>Symbole</th>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'left' }}>Nom</th>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'left' }}>Secteur</th>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'right' }}>Titres Total</th>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'right' }}>Titres Flottant</th>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'center' }}>Flottant %</th>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'center' }}>15% OK</th>
                  <th style={{ color: 'white', padding: '12px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredSocietes.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                      🏢 Aucune société trouvée
                    </td>
                  </tr>
                ) : filteredSocietes.map((soc, index) => (
                  <tr key={soc.symbole} style={{ backgroundColor: index % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        backgroundColor: '#667eea',
                        color: 'white',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                        {soc.symbole}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#1e293b', fontWeight: 500, fontSize: '13px' }}>
                      {soc.nom_societe || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#475569', fontSize: '13px' }}>
                      {soc.secteur || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#334155' }}>
                      {formatNumber(soc.quantite_totale_titres)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#334155' }}>
                      {formatNumber(soc.quantite_titres_flottant)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {soc.flottant_pct !== null && soc.flottant_pct !== undefined ? (
                        <span style={{
                          background: soc.flottant_pct >= 15 ? '#dcfce7' : '#fee2e2',
                          color: soc.flottant_pct >= 15 ? '#16a34a' : '#dc2626',
                          padding: '3px 8px',
                          borderRadius: '10px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          {soc.flottant_pct.toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {soc.diffusion_15pct_respectee === true ? (
                        <span style={{ color: '#16a34a', fontSize: '16px' }}>✓</span>
                      ) : soc.diffusion_15pct_respectee === false ? (
                        <span style={{ color: '#dc2626', fontSize: '16px' }}>✗</span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleEdit(soc)}
                        style={{
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          padding: '5px 12px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        Modifier
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Box>

      {/* Edit Modal */}
      {editingSociete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div style={{ background: 'var(--bg-box)', padding: '24px', borderRadius: '12px', width: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>Modifier {editingSociete.symbole}</h3>

            {/* Basic Info */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Nom Société</label>
              <input
                type='text'
                value={editingSociete.nom_societe || ''}
                onChange={(e) => setEditingSociete({ ...editingSociete, nom_societe: e.target.value })}
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Secteur</label>
                <input
                  type='text'
                  value={editingSociete.secteur || ''}
                  onChange={(e) => setEditingSociete({ ...editingSociete, secteur: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Pays</label>
                <input
                  type='text'
                  value={editingSociete.pays || ''}
                  onChange={(e) => setEditingSociete({ ...editingSociete, pays: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                />
              </div>
            </div>

            {/* Capitalisation Section */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#475569' }}>📊 Capitalisation & Flottant</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Titres Total</label>
                  <input
                    type='number'
                    value={editingSociete.quantite_totale_titres || ''}
                    onChange={(e) => setEditingSociete({ ...editingSociete, quantite_totale_titres: e.target.value ? Number(e.target.value) : null })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    placeholder="Ex: 100000000"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Titres Flottant</label>
                  <input
                    type='number'
                    value={editingSociete.quantite_titres_flottant || ''}
                    onChange={(e) => setEditingSociete({ ...editingSociete, quantite_titres_flottant: e.target.value ? Number(e.target.value) : null })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    placeholder="Ex: 15000000"
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Flottant %</label>
                  <input
                    type='number'
                    step="0.01"
                    value={editingSociete.flottant_pct || ''}
                    onChange={(e) => setEditingSociete({ ...editingSociete, flottant_pct: e.target.value ? Number(e.target.value) : null })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                    placeholder="Ex: 15.5"
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: '#64748b', display: 'block', marginBottom: '4px' }}>Conformité 15%</label>
                  <select
                    value={editingSociete.diffusion_15pct_respectee === null ? '' : editingSociete.diffusion_15pct_respectee ? 'true' : 'false'}
                    onChange={(e) => setEditingSociete({
                      ...editingSociete,
                      diffusion_15pct_respectee: e.target.value === '' ? null : e.target.value === 'true'
                    })}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <option value="">Non renseigné</option>
                    <option value="true">✓ Oui (≥15%)</option>
                    <option value="false">✗ Non (&lt;15%)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button
                onClick={() => setEditingSociete(null)}
                style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', cursor: 'pointer', fontWeight: 500 }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
};

export default AdminSocietesScreen;


